import { Region } from '../types';

// Haversine formula to calculate distance between two coordinates in kilometers
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Find the closest city to the user's location
export function findClosestCity(
  userLat: number,
  userLng: number,
  regions: Region[]
): { region: Region; distance: number } | null {
  if (regions.length === 0) return null;

  let closestRegion = regions[0];
  let minDistance = calculateDistance(
    userLat,
    userLng,
    regions[0].center.lat,
    regions[0].center.lng
  );

  for (const region of regions.slice(1)) {
    const distance = calculateDistance(
      userLat,
      userLng,
      region.center.lat,
      region.center.lng
    );
    if (distance < minDistance) {
      minDistance = distance;
      closestRegion = region;
    }
  }

  return { region: closestRegion, distance: minDistance };
}

// Maximum distance (in km) to use a detected city
export const MAX_DETECTION_DISTANCE_KM = 300;

// Default city slug when geolocation fails or distance is too far
export const DEFAULT_CITY_SLUG = 'singapore';

// Get user's geolocation as a Promise
export function getUserLocation(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  });
}

// Detect user's city based on geolocation
export async function detectUserCity(
  regions: Region[]
): Promise<{ slug: string; isAuto: boolean; tooFar: boolean }> {
  try {
    const userLocation = await getUserLocation();
    const closest = findClosestCity(userLocation.lat, userLocation.lng, regions);

    if (!closest) {
      return { slug: DEFAULT_CITY_SLUG, isAuto: true, tooFar: true };
    }

    // If the closest city is more than 300km away, default to Singapore
    if (closest.distance > MAX_DETECTION_DISTANCE_KM) {
      return { slug: DEFAULT_CITY_SLUG, isAuto: true, tooFar: true };
    }

    return { slug: closest.region.slug, isAuto: true, tooFar: false };
  } catch {
    // Geolocation failed or denied
    return { slug: DEFAULT_CITY_SLUG, isAuto: true, tooFar: false };
  }
}
